import * as Toast from "@radix-ui/react-toast"
import { waitFor } from "xstate"
import { useEffect } from "react"
import { useSelector } from "@xstate/react"
import { isRouteErrorResponse, Outlet, redirect, useNavigate, useRouteError } from "react-router-dom"

import { getRedirectPath } from "@recordsure/unity-auth-machine"

import { Header } from "../components"

import { authMachine, hasPermission } from "../machines/AuthMachine"

async function loader() {
  await waitFor(authMachine, (snapshot) => snapshot.hasTag("Settled"))

  const snapshot = authMachine.getSnapshot()

  if (snapshot.hasTag("AuthIncomplete")) {
    const redirectPath = getRedirectPath()
    return redirect(`/auth${redirectPath === "/" ? "" : `?from=${redirectPath}`}`)
  }

  if (hasPermission("readAccess")) return null

  throw new Response("Unauthorized", { status: 401 })
}

const Route = () => {
  const navigate = useNavigate()

  const isAuthErrored = useSelector(authMachine, (snapshot) => snapshot.matches("error"))

  useEffect(() => {
    const subscription = authMachine.on("UNAUTHENTICATED", () => navigate("/auth"))
    return () => subscription.unsubscribe()
  }, [navigate])

  return (
    <Toast.Provider duration={Infinity}>
      <Outlet />
      <Toast.Viewport className="fixed space-y-2 right-3 bottom-3 max-w-md w-full rounded-md PageProgressIndex [&>*]:overflow-hidden [&>*]:rounded-[inherit]" />
      {isAuthErrored && (
        <Toast.Root open type="background" className="bg-base-100 w-full">
          <div
            style={{ gridTemplateAreas: `"title action" "description action"` }}
            className="shadow p-4 bg-warning/10 grid grid-cols-[minmax(0,1fr)_max-content]"
          >
            <Toast.Title
              className="font-semibold tracking-wide text-warning-content dark:text-base-content"
              style={{ gridArea: "title" }}
            >
              Authentication Error
            </Toast.Title>
            <Toast.Description style={{ gridArea: "description" }} className="text-xs">
              Error refreshing account session. Use the button to manually retry the process and prevent data loss.
            </Toast.Description>
            <div style={{ gridArea: "action" }}>
              <Toast.Action
                altText="Reload the page"
                onClick={() => authMachine.send({ type: "EXTERNAL_LOGIN" })}
                className="btn btn-xs btn-outline rounded normal-case btn-warning"
              >
                Log In
              </Toast.Action>
            </div>
          </div>
        </Toast.Root>
      )}
    </Toast.Provider>
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()

  console.error(error)

  return (
    <div className="flex h-screen w-full flex-col items-start justify-start gap-3 overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5">
      <Header />
      <div className="divider my-0" />
      <div className="flex w-full grow flex-col items-center justify-center gap-7 p-4">
        <div className="text-black max-w-sm space-y-4 text-center">
          {isRouteErrorResponse(error) ? (
            <>
              <h1 className="text-7xl font-bold text-accent">{error.status}</h1>
              {[401, 403].includes(error.status) ? (
                <>
                  <h2 className="text-xl font-bold">
                    Sorry, you do not have the permissions to{" "}
                    {error.status === 401 ? "view this page" : "perform this request"}.
                  </h2>
                  <p>
                    If you feel you have reached this page in error, please speak to your IT support personnel to get
                    help with this issue.
                  </p>
                </>
              ) : error.status === 423 ? (
                <>
                  <h2 className="text-3xl font-bold">The resource you&apos;re trying to access is currently locked.</h2>
                  <p>Please speak to your IT support personnel to get help with this issue</p>
                </>
              ) : (
                <h2 className="text-black text-center text-3xl font-bold">Something went wrong!!</h2>
              )}
              <p className="font-medium text-error">
                Reason:{" "}
                {typeof error.data === "string" ? error.data : error instanceof Error ? error.message : "Unknown"}
              </p>
            </>
          ) : (
            <h2 className="text-black text-center text-3xl font-bold">Something went wrong!!</h2>
          )}
        </div>
      </div>
    </div>
  )
}

Route.loader = loader
Route.ErrorBoundary = ErrorBoundary

export default Route

declare global {
  function triggerSessionRefresh(): void
}
